/**
 * Types for the OpenWrench agent harness (ow-agent-harness-service) — the
 * chat-agent backend the in-app assistant panel talks to. The service holds the
 * OpenWrench JWT server-side; the browser authenticates with an httpOnly
 * session cookie obtained by exchanging the web app's own X-Auth-Token
 * (POST /api/login/token). All requests must send credentials.
 */

export interface AgentSessionInfo {
	email: string;
	role: string;
	/** Harness session id. Browsers ride the cookie; native clients store this. */
	sid?: string;
}

export interface AgentModelChoice {
	key: string;
	name: string;
	provider: string;
	reasoning: boolean;
}

export interface AgentModelsResponse {
	models: AgentModelChoice[];
	default: string;
	thinkingLevels: string[];
	defaultThinkingLevel: string;
	externalFileOcr: boolean;
}

export interface AgentConversationInfo {
	id: string;
	model: string;
	thinkingLevel: string;
	toolset: string;
}

export interface AgentUploadResponse {
	/** Server-side path the agent's file tools read; referenced in message text. */
	path: string;
	name: string;
	size: number;
}

export interface AgentPendingApproval {
	id: string;
	conversationId: string;
	toolCallId: string;
	toolName: string;
	args: unknown;
	destructive: boolean;
	createdAt: number;
	/**
	 * The model's one-sentence "why" for the write, kept apart from `args`: it is
	 * display text, never part of what runs. Absent from older harnesses and from
	 * a model that skipped it.
	 */
	rationale?: string;
}

/**
 * A harness action-journal record (`src/actions.ts` in the harness). Only the
 * verification states matter here: `outcome_unknown` means a write was sent and
 * its result was lost, and every later call of that tool is paused for this
 * login until the user records what actually happened.
 */
export type AgentActionState =
	| 'awaiting_approval'
	| 'approved'
	| 'executing'
	| 'succeeded'
	| 'cancelled'
	| 'outcome_unknown'
	| 'confirmed_applied'
	| 'confirmed_not_applied';

export interface AgentAction {
	id: string;
	toolName: string;
	state: AgentActionState;
	conversationId: string;
	requestId: string;
	createdAt: string;
	updatedAt: string;
}

export type AgentActionOutcome = 'confirmed_applied' | 'confirmed_not_applied';

/**
 * The harness tool that offers to send a request Oscar cannot handle to the
 * OpenWrench team (`src/team-requests.ts` in the harness). Its approval is the
 * customer's "send this?", so it gets its own card rather than the generic one.
 */
export const AGENT_TEAM_REQUEST_TOOL = 'send_team_request';

/** `missing_capability`: no tool can do it. `unanswered`: Oscar tried and could not. */
export type AgentTeamRequestKind = 'missing_capability' | 'unanswered';

/**
 * SSE events from GET /api/conversations/:id/events. Assistant events carry the
 * id of the message they belong to and are cumulative, so replay after a
 * dropped stream updates bubbles in place instead of duplicating them.
 */
export type AgentStreamEvent =
	| {
			type: 'hello';
			busy: boolean;
			model: string;
			thinkingLevel: string;
			toolset: string;
			pending: AgentPendingApproval[];
	  }
	| { type: 'gap'; reason?: string }
	| { type: 'user_text'; id: string; text: string }
	| { type: 'assistant_thinking'; id: string; text: string }
	| { type: 'assistant_text'; id: string; text: string }
	| { type: 'assistant_done'; id: string; text: string; thinking: string; toolCalls: number }
	| { type: 'tool_start'; id: string; name: string; args: unknown }
	| { type: 'tool_end'; id: string; name: string; isError: boolean; summary: string }
	| ({ type: 'approval_required' } & AgentPendingApproval)
	| { type: 'approval_resolved'; id: string; toolCallId: string; approved: boolean }
	| { type: 'auth_error' }
	| { type: 'action'; action: AgentAction }
	| { type: 'idle'; error: string | null; silent: boolean }
	| { type: 'file'; id: string; name: string; size: number; url: string }
	/** A team request reached the OpenWrench team; `id` is the tool-call id. */
	| { type: 'team_request'; id: string; kind: AgentTeamRequestKind; request: string }
	| { type: 'thinking_level'; thinkingLevel: string }
	| { type: 'model'; model: string }
	/**
	 * The harness retried the turn on a stronger model. `discarded` names what the
	 * abandoned attempt already streamed; the retry starts the answer over, so
	 * those messages must not read as the reply.
	 */
	| {
			type: 'escalated';
			from: string;
			to: string;
			thinkingLevel: string;
			rung: number;
			reason: string;
			discarded?: { messages: string[]; tools: string[] };
	  };

/** One rendered row of the chat transcript, keyed by a stable id for replay. */
export type AgentTranscriptEntry =
	| { kind: 'user'; id: string; text: string }
	| {
			kind: 'assistant';
			id: string;
			text: string;
			thinking: string;
			done: boolean;
			/** Written by an attempt the harness abandoned for a stronger model. */
			superseded?: boolean;
	  }
	| {
			kind: 'tool';
			id: string;
			name: string;
			args: unknown;
			summary: string;
			isError: boolean;
			done: boolean;
			/** Made by an attempt the harness abandoned for a stronger model. */
			superseded?: boolean;
	  }
	| {
			kind: 'approval';
			id: string;
			toolCallId: string;
			toolName: string;
			args: unknown;
			destructive: boolean;
			resolved: boolean;
			approved: boolean;
			rationale?: string;
	  }
	| { kind: 'file'; id: string; name: string; size: number; url: string }
	| { kind: 'team_request'; id: string; requestKind: AgentTeamRequestKind; request: string }
	| {
			kind: 'action_review';
			/** The action id. */
			id: string;
			toolName: string;
			createdAt: string;
			state: AgentActionState;
	  }
	| { kind: 'notice'; id: string; severity: 'info' | 'error'; text: string };

/**
 * Saved history: GET /api/history. The harness relays the OpenWrench
 * intelligence service under the signed-in person's identity, so these are only
 * ever that person's own conversations. A saved conversation's id is the
 * conversation's own id: opening it rebuilds it on the harness.
 */
export interface AgentHistoryConversation {
	id: string;
	title: string | null;
	archived: boolean;
	created_at: string;
	updated_at: string;
}

export interface AgentHistoryPage {
	items: AgentHistoryConversation[];
	next_cursor: number | null;
	/** False when this session's history is not stored at all. */
	stored: boolean;
}
